import type { useSendOTP } from '@goodneighbor/api-client';
import type { CountryCode } from 'libphonenumber-js/max';

import { useCallback } from 'react';

import { useAuthStore } from '@goodneighbor/auth/hooks';

import { useAdminOTP } from '@goodneighbor/api-client';
import { parseToInternationalPhoneNumber } from '@goodneighbor/auth/utils';

type UseSendOTPReturn = ReturnType<typeof useSendOTP>;
type ExtraPhoneLoginData = Parameters<
  UseSendOTPReturn['mutateAsync']
>[0]['data'];

type PhoneLoginData = {
  phone: string;
  countryCode?: CountryCode;
} & ExtraPhoneLoginData;

/**
 * @returns function to be used to login with phone # providing OTP support.
 * If countryCode is not set, US is used as a default
 */
const useAdminLoginWithPersistence = () => {
  const { mutateAsync, ...mutationMetadata } = useAdminOTP();
  const setTokens = useAuthStore((state) => state.setTokens);

  const handleLogInOtpWithPersistance = useCallback(
    async ({ phone, countryCode, ...rest }: PhoneLoginData) => {
      const internationalPhoneNumber = parseToInternationalPhoneNumber(
        phone,
        countryCode
      );

      const { data } = await mutateAsync({
        data: {
          phone: internationalPhoneNumber,
          ...rest
        }
      });

      setTokens(data.tokens);

      return data;
    },
    [mutateAsync, setTokens]
  );

  return { handleLogInOtpWithPersistance, ...mutationMetadata };
};

export { useAdminLoginWithPersistence };
